import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

type VhString = `${number}vh`;

export interface AutosizeOptions {
    maxHeight?: number | VhString;
};

const DEFAULT_MAX_VH = 40;

function toPixels(maxHeight: number | VhString | undefined): number {
    if (typeof maxHeight === "number") return maxHeight;
    const vh = typeof maxHeight === "string" ? parseFloat(maxHeight) : DEFAULT_MAX_VH;
    return Math.round((window.innerHeight * (isNaN(vh) ? DEFAULT_MAX_VH : vh)) / 100);
}

export function useAutosizeTextarea<T extends HTMLTextAreaElement = HTMLTextAreaElement>(
    value: string,
    options: AutosizeOptions = { maxHeight: `${DEFAULT_MAX_VH}vh` }
) {
    const textareaRef = useRef<T | null>(null);

    const resize = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;

        el.style.height = "auto";
        const capPx = toPixels(options.maxHeight);
        el.style.height = Math.min(el.scrollHeight, capPx) + "px";
    }, [options.maxHeight]);

    useLayoutEffect(resize, [value, resize]);

    useEffect(() => {
        const onWinResize = () => resize();
        window.addEventListener("resize", onWinResize);
        return () => window.removeEventListener("resize", onWinResize);
    }, [resize]);

    return { textareaRef, onInput: resize, resize };
};